import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useConversations } from '../context/ConversationContext';
import { API_BASE_URL } from '../config';
import { useTranslation } from 'react-i18next';
import styles from './Conversation.module.css';
import DatePlanning from '../components/DatePlanning';  // ⭐ NOUVEAU

const Conversation = () => {
  const { t, i18n } = useTranslation(['conversation', 'common']);
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, refreshUserProfile } = useConversations();
  const messagesEndRef = useRef(null);
  
  // États locaux
  const [conversation, setConversation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  
  // Mode de la conversation
  const [mode, setMode] = useState('ice_breaker');
  
  // Historique complet
  const [history, setHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(true);
  
  // États Ice Breaker
  const [showStyleChoiceIceBreaker, setShowStyleChoiceIceBreaker] = useState(true);
  const [iceBreakers, setIceBreakers] = useState([]);
  const [loadingIceBreaker, setLoadingIceBreaker] = useState(false);
  
  // États Active Chat
  const [girlMessage, setGirlMessage] = useState('');
  const [wingmanAnalysis, setWingmanAnalysis] = useState(null);
  const [wingmanLoading, setWingmanLoading] = useState(false);
  const [showStyleChoice, setShowStyleChoice] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [wingmanInteractionId, setWingmanInteractionId] = useState(null);
  const [showDatePlanning, setShowDatePlanning] = useState(false);
  const [datePlanningData, setDatePlanningData] = useState(null);
  // ⭐ NOUVEAUX ÉTATS : Upload d'image
  const [uploadMode, setUploadMode] = useState('text'); // 'text' ou 'image'
  const [imagePreview, setImagePreview] = useState(null);
  const [imageFile, setImageFile] = useState(null);
  const fileInputRef = useRef(null);

  // Scroll auto
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [history, wingmanAnalysis, suggestions]);

  // Charger conversation + historique
  useEffect(() => {
    loadConversation();
    loadHistory();
  }, [id]);

  const loadConversation = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      const response = await fetch(`${API_BASE_URL}/conversations/${id}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) throw new Error(t('conversation:errors.conversation_not_found'));

      const data = await response.json();
      const conv = data.conversation;
      setConversation(conv);
      
      // ⚠️ Le mode sera défini par restoreWingmanState() après le chargement de l'historique
      
    } catch (err) {
      console.error('Erreur chargement conversation:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // ⭐ FONCTION : Ouvrir Date Planning
 const handleOpenDatePlanning = async () => {
  try {
    const token = localStorage.getItem('token');
    
    const response = await fetch(`${API_BASE_URL}/wingman/plan-date`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        conversation_id: id,
        user_suggestion: null,
        language: i18n.language
      })
    });

    const data = await response.json();
    
    if (data.success) {
      setShowDatePlanning(true);
      
      // ⭐ STOCKER AUSSI wingman_interaction_id
      setDatePlanningData({
        date_planning_id: data.date_planning_id,
        wingman_interaction_id: data.wingman_interaction_id,  // ⭐ AJOUTER CECI
        analysis: data.analysis
      });
      
      await refreshUserProfile();
    }
    
  } catch (err) {
    console.error('Erreur:', err);
  }
};

  // ⭐ FONCTION : Fermer Date Planning
  const handleCloseDatePlanning = () => {
    setShowDatePlanning(false);
  };

  // ⭐ FONCTION : Analyse depuis image
  const handleAnalyzeFromImage = async () => {
    if (!imageFile) {
      setError(t('conversation:errors.select_screenshot'));
      return;
    }

    setWingmanLoading(true);
    setError('');

    try {
      const token = localStorage.getItem('token');
      const base64 = await fileToBase64(imageFile);
      
      const response = await fetch(`${API_BASE_URL}/wingman/analyze-image`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          conversation_id: id,
          image_base64: base64,
          mime_type: imageFile.type
        })
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setGirlMessage(data.extracted_message);
        setWingmanAnalysis(data.analysis);
        setWingmanInteractionId(data.wingman_interaction_id);
        await refreshUserProfile();
        await loadHistory();
      } else {
        if (response.status === 402) {
          setError(
            t('conversation:errors.not_enough_credits_with_required', {
              required: data.required,
            })
          );
        } else {
          setError(data.message || t('conversation:errors.image_analysis_failed'));
        }
      }
    } catch (err) {
      console.error('Erreur analyse image:', err);
      setError(t('conversation:errors.connection_error'));
    } finally {
      setWingmanLoading(false);
    }
  };

  // ⭐ UTILITAIRE : Convertir fichier en base64
  const fileToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        const base64 = reader.result.split(',')[1];
        resolve(base64);
      };
      reader.onerror = (error) => reject(error);
    });
  };

  // ⭐ FONCTION : Gérer sélection d'image
  const handleImageSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      setError(t('conversation:errors.only_images_allowed'));
      return;
    }

    const maxSize = 10 * 1024 * 1024;
    if (file.size > maxSize) {
      setError(t('conversation:errors.image_too_large'));
      return;
    }

    const reader = new FileReader();
    reader.onloadend = () => {
      setImagePreview(reader.result);
      setImageFile(file);
      setError('');
    };
    reader.readAsDataURL(file);
  };

  // ⭐ FONCTION : Supprimer image
  const handleRemoveImage = () => {
    setImagePreview(null);
    setImageFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // ⭐ FONCTION : Toggle mode
  const handleToggleUploadMode = (mode) => {
    setUploadMode(mode);
    setError('');
    if (mode === 'text') {
      handleRemoveImage();
    } else {
      setGirlMessage('');
    }
  };

  // ============ CHARGER HISTORIQUE + RESTAURER ÉTAT ============
  const loadHistory = async () => {
    try {
      setLoadingHistory(true);
      const token = localStorage.getItem('token');
      
      const response = await fetch(`${API_BASE_URL}/wingman/history/${id}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        const interactions = data.interactions || [];
        setHistory(interactions);
        
        console.log('📜 Historique chargé:', interactions.length, 'interactions');

        // ✅ RESTAURER L'ÉTAT DU WINGMAN
        restoreWingmanState(interactions);
      }
      
    } catch (err) {
      console.error('Erreur chargement historique:', err);
    } finally {
      setLoadingHistory(false);
    }
  };

  // ============ NOUVELLE FONCTION : RESTAURER L'ÉTAT ============
  const restoreWingmanState = (interactions) => {
    console.log('🔄 Restauration état Wingman, nombre d\'interactions:', interactions.length);

    // CAS 0 : Aucun historique → Mode ice_breaker
    if (interactions.length === 0) {
      console.log('📭 Aucun historique, mode ice_breaker');
      setMode('ice_breaker');
      return;
    }

    // Trouver la dernière interaction
    const lastInteraction = interactions[interactions.length - 1];
    
    console.log('🔄 Dernière interaction:', {
      type: lastInteraction.interaction_type,
      has_girl_message: !!lastInteraction.girl_message,
      has_analysis: !!lastInteraction.analysis_summary,
      has_suggestions: !!lastInteraction.suggestions_generated,
      suggestions_length: lastInteraction.suggestions_generated?.length || 0,
      message_used: !!lastInteraction.message_used_content
    });

    // CAS 1 : Dernière interaction = ICE BREAKER
    if (lastInteraction.interaction_type === 'ice_breaker') {
      const hasValidSuggestions = lastInteraction.suggestions_generated && 
                                  Array.isArray(lastInteraction.suggestions_generated) &&
                                  lastInteraction.suggestions_generated.length > 0;
      
      if (hasValidSuggestions && !lastInteraction.message_used_content) {
        console.log('✅ Restauration ice breakers non utilisés');
        setIceBreakers(lastInteraction.suggestions_generated);
        setShowStyleChoiceIceBreaker(false);
        setMode('ice_breaker');
        return;
      } else if (lastInteraction.message_used_content) {
        console.log('✅ Ice breaker utilisé, mode waiting_response');
        setMode('waiting_response');
        return;
      }
    }
    if (lastInteraction.interaction_type === 'date_planning') {
      console.log('📅 Date Planning dans l\'historique');
      
      if (lastInteraction.message_used_content) {
        console.log('✅ Suggestion utilisée:', lastInteraction.message_used_content.substring(0, 50));
      }
      
      setMode('waiting_response');
      return;
    }

    // CAS 2 : Il y a des échanges (girl_message)
    if (lastInteraction.girl_message) {
      const hasValidSuggestions = lastInteraction.suggestions_generated && 
                                  Array.isArray(lastInteraction.suggestions_generated) &&
                                  lastInteraction.suggestions_generated.length > 0;
      
      // CAS 2A : Suggestions générées mais pas utilisées
      if (hasValidSuggestions && !lastInteraction.message_used_content) {
        console.log('✅ Restauration suggestions non utilisées');
        
        setGirlMessage(lastInteraction.girl_message);
        
        const analysis = {
          summary: lastInteraction.analysis_summary,
          emotion: lastInteraction.detected_emotion,
          indicators: (() => { 
            try { 
              return typeof lastInteraction.engagement_indicators === 'string' 
                ? JSON.parse(lastInteraction.engagement_indicators) 
                : (Array.isArray(lastInteraction.engagement_indicators) ? lastInteraction.engagement_indicators : []); 
            } catch { 
              return []; 
            } 
          })(),
          recommendation: lastInteraction.recommendation_text,
          ghosting_alert: lastInteraction.is_ghosting ? {
            is_ghosting: lastInteraction.is_ghosting,
            ghosting_level: lastInteraction.ghosting_level,
            red_flags: (() => {
              try {
                return typeof lastInteraction.red_flags === 'string' 
                  ? JSON.parse(lastInteraction.red_flags) 
                  : (Array.isArray(lastInteraction.red_flags) ? lastInteraction.red_flags : []);
              } catch {
                return [];
              }
            })(),
            strategy: lastInteraction.strategy_recommended
          } : null,
          date_readiness_alert: lastInteraction.should_suggest_date ? {
            should_suggest_date: lastInteraction.should_suggest_date,
            score_estimate: lastInteraction.date_readiness_score,
            reasoning: lastInteraction.date_readiness_reasoning
          } : null
        };
        setWingmanAnalysis(analysis);
        
        setSuggestions(lastInteraction.suggestions_generated);
        setWingmanInteractionId(lastInteraction.id);
        
        setMode('active_chat');
        setShowStyleChoice(false);
        
        console.log('📥 État restauré : suggestions affichées');
        return;
      }
      
      // CAS 2B : Analyse faite mais pas de génération (ou array vide)
      if (lastInteraction.analysis_summary && 
          !hasValidSuggestions &&
          !lastInteraction.message_used_content) {
        
        console.log('✅ Restauration analyse sans suggestions');
        
        setGirlMessage(lastInteraction.girl_message);
        
        const analysis = {
          summary: lastInteraction.analysis_summary,
          emotion: lastInteraction.detected_emotion,
          indicators: (() => { 
            try { 
              return typeof lastInteraction.engagement_indicators === 'string' 
                ? JSON.parse(lastInteraction.engagement_indicators) 
                : (Array.isArray(lastInteraction.engagement_indicators) ? lastInteraction.engagement_indicators : []); 
            } catch { 
              return []; 
            } 
          })(),
          recommendation: lastInteraction.recommendation_text,
          ghosting_alert: lastInteraction.is_ghosting ? {
            is_ghosting: lastInteraction.is_ghosting,
            ghosting_level: lastInteraction.ghosting_level,
            red_flags: (() => {
              try {
                return typeof lastInteraction.red_flags === 'string' 
                  ? JSON.parse(lastInteraction.red_flags) 
                  : (Array.isArray(lastInteraction.red_flags) ? lastInteraction.red_flags : []);
              } catch {
                return [];
              }
            })(),
            strategy: lastInteraction.strategy_recommended
          } : null,
          date_readiness_alert: lastInteraction.should_suggest_date ? {
            should_suggest_date: lastInteraction.should_suggest_date,
            score_estimate: lastInteraction.date_readiness_score,
            reasoning: lastInteraction.date_readiness_reasoning
          } : null
        };
        setWingmanAnalysis(analysis);
        
        setWingmanInteractionId(lastInteraction.id);
        
        setMode('active_chat');
        setShowStyleChoice(false);
        setSuggestions([]);
        
        console.log('📥 État restauré : analyse affichée, bouton générer visible');
        return;
      }
      
      // CAS 2C : Message utilisé
      if (lastInteraction.message_used_content) {
        console.log('✅ Message utilisé, mode waiting_response');
        setMode('waiting_response');
        return;
      }
    }

    // CAS PAR DÉFAUT
    if (interactions.length > 0) {
      console.log('✅ Historique détecté, mode active_chat par défaut');
      setMode('active_chat');
    } else {
      console.log('📭 Pas d\'historique, mode ice_breaker');
      setMode('ice_breaker');
    }
  };

  // ============ ICE BREAKER ============
  const handleGenerateIceBreakers = async (style) => {
    setLoadingIceBreaker(true);
    setError('');

    try {
      const token = localStorage.getItem('token');
      
    const response = await fetch(`${API_BASE_URL}/wingman/ice-breaker`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        conversation_id: id,
        style: style,
        language: user?.language || i18n.language // ✅ AJOUTER CETTE LIGNE
      })
    });

      const data = await response.json();

      if (response.ok && data.success) {
        setIceBreakers(data.suggestions);
        setShowStyleChoiceIceBreaker(false);
        await refreshUserProfile();
        await loadHistory();
        
      } else {
        if (response.status === 402) {
          setError(
            t('conversation:errors.not_enough_credits_with_required', {
              required: data.required,
            })
          );
        } else {
          setError(data.message || t('conversation:errors.generation_failed'));
        }
      }
      
    } catch (err) {
      console.error('Erreur génération ice breakers:', err);
      setError(t('conversation:errors.connection_error'));
    } finally {
      setLoadingIceBreaker(false);
    }
  };

  const handleUseIceBreaker = async (message, messageId) => {
    await navigator.clipboard.writeText(message);
    
    try {
      const token = localStorage.getItem('token');
      
      await fetch(`${API_BASE_URL}/wingman/mark-sent`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          conversation_id: id,
          message_sent: message
        })
      });

      const lastInteraction = history[history.length - 1];
      if (lastInteraction) {
        await fetch(`${API_BASE_URL}/wingman/mark-used`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            wingman_interaction_id: lastInteraction.id,
            message_id: messageId,
            message_content: message
          })
        });
      }

      setMode('waiting_response');
      await loadHistory();
      
    } catch (err) {
      console.error('Erreur marking sent:', err);
    }
  };

  // ============ WAITING RESPONSE ============
  const handleSheReplied = () => {
    setMode('active_chat');
    // Reset pour nouvelle interaction
    setGirlMessage('');
    setWingmanAnalysis(null);
    setSuggestions([]);
    setWingmanInteractionId(null);
  };

  // ============ ACTIVE CHAT ============
  const handleAnalyze = async () => {
    if (!girlMessage.trim()) {
      setError(t('conversation:errors.enter_message_first'));
      return;
    }

    setWingmanLoading(true);
    setError('');

    try {
      const token = localStorage.getItem('token');
      
     const response = await fetch(`${API_BASE_URL}/wingman/analyze`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          conversation_id: id,
          girl_message: girlMessage,
          language: user?.language || i18n.language // ✅ AJOUTER CETTE LIGNE
        })
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setWingmanAnalysis(data.analysis);
        setWingmanInteractionId(data.wingman_interaction_id);
        await refreshUserProfile();
        await loadHistory();
        
      } else {
        if (response.status === 402) {
          setError(
            t('conversation:errors.not_enough_credits_with_required', {
              required: data.required,
            })
          );
        } else {
          setError(data.message || t('conversation:errors.analysis_failed'));
        }
      }
      
    } catch (err) {
      console.error('Erreur analyse Wingman:', err);
      setError(t('conversation:errors.connection_error'));
    } finally {
      setWingmanLoading(false);
    }
  };

  const handleShowStyleChoice = () => {
    setShowStyleChoice(true);
  };

  const handleGenerate = async (style) => {
    setWingmanLoading(true);
    setError('');

    try {
      const token = localStorage.getItem('token');
      
      const response = await fetch(`${API_BASE_URL}/wingman/generate`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          conversation_id: id,
          style: style,
          wingman_interaction_id: wingmanInteractionId,
          language: user?.language || i18n.language // ✅ AJOUTER CETTE LIGNE
        })
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setSuggestions(data.suggestions);
        setShowStyleChoice(false);
        await refreshUserProfile();
        await loadHistory();
        
      } else {
        if (response.status === 402) {
          setError(
            t('conversation:errors.not_enough_credits_with_required', {
              required: data.required,
            })
          );
        } else {
          setError(data.message || t('conversation:errors.generation_failed'));
        }
      }
      
    } catch (err) {
      console.error('Erreur génération Wingman:', err);
      setError(t('conversation:errors.connection_error'));
    } finally {
      setWingmanLoading(false);
    }
  };

  const handleUseMessage = async (message, messageId) => {
    await navigator.clipboard.writeText(message);
    
    try {
      const token = localStorage.getItem('token');
      
      await fetch(`${API_BASE_URL}/wingman/mark-used`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          wingman_interaction_id: wingmanInteractionId,
          message_id: messageId,
          message_content: message
        })
      });

      await loadHistory();
      alert(t('conversation:alerts.message_copied_and_marked'));
      
    } catch (err) {
      console.error('Erreur mark used:', err);
    }
  };

  const handleNewInteraction = () => {
    setGirlMessage('');
    setWingmanAnalysis(null);
    setSuggestions([]);
    setShowStyleChoice(false);
    setWingmanInteractionId(null);
    setError('');
  };

  const handleCopy = (text) => {
    navigator.clipboard.writeText(text)
      .then(() => {
        alert(t('conversation:alerts.copied_to_clipboard'));
      })
      .catch(err => {
        console.error('Erreur copie:', err);
        alert(t('conversation:ice_breaker.copy_error'));
      });
  };

  const changeLanguage = (lng) => {
    i18n.changeLanguage(lng);
  };

  // Loading
  if (loading) {
    return (
      <div className={styles["conversation-page"]}>
        <div className={styles["conversation-loading"]}>
          <div className={styles["spinner-large"]}></div>
          <p>{t('conversation:history.loading')}</p>
        </div>
      </div>
    );
  }

  // Error
  if (!conversation) {
    return (
      <div className={styles["conversation-page"]}>
        <div className={styles["conversation-error"]}>
          <h2>{t('conversation:error.title')}</h2>
          <p>{error || t('conversation:errors.conversation_not_found')}</p>
          <button onClick={() => navigate('/dashboard')}>
            {t('conversation:header.back_button')}
          </button>
        </div>
      </div>
    );
  }

  const girlInfo = typeof conversation.girl_info === 'string' 
    ? JSON.parse(conversation.girl_info) 
    : conversation.girl_info;

  return (
    <div className={styles["conversation-page"]}>
      
      {/* ⭐ MODAL DATE PLANNING */}
 {showDatePlanning && datePlanningData && (
  <DatePlanning
    conversationId={id}
    girlInfo={girlInfo}
    girlName={girlInfo?.name}
    datePlanningData={datePlanningData}
    onClose={handleCloseDatePlanning}
    refreshCredits={refreshUserProfile}   // optionnel mais logique
    onMessageUsed={loadHistory}           // pour recharger l'historique
  />
)}


      {/* HEADER FIXE */}
      <div className={styles["conversation-header-fixed"]}>
        <button 
          className={styles["btn-back-header"]} 
          onClick={() => navigate('/dashboard')}
        >
          {t('conversation:header.back_button')}
        </button>

        <div className={styles["header-info"]}>
          <h2>{girlInfo?.name || conversation.title}</h2>
          <span className={styles["platform-badge"]}>
            {girlInfo?.platform || 'Instagram'}
          </span>
        </div>

        <div className={styles["header-right"]}>
          <div className={styles["credits-display"]}>
            <span className={styles["credits-icon"]}>💎</span>
            <span className={styles["credits-count"]}>
              {user?.remaining_credits || 0}
            </span>
          </div>

          {/* Sélecteur de langue 
          <div className={styles["lang-switcher"]}>
            <button
              className={
                i18n.language === 'fr' 
                  ? styles["lang-btn-active"] 
                  : styles["lang-btn"]
              }
              onClick={() => changeLanguage('fr')}
            >
              🇫🇷
            </button>
            <button
              className={
                i18n.language === 'en' 
                  ? styles["lang-btn-active"] 
                  : styles["lang-btn"]
              }
              onClick={() => changeLanguage('en')}
            >
              🇬🇧
            </button>
          </div>
          */}


        </div>
      </div>

      {/* CONTENU PRINCIPAL */}
      <div className={styles["conversation-content"]}>

        {/* Alert erreur */}
        {error && (
          <div className={`${styles["alert"]} ${styles["alert-error"]}`}>
            <span className={styles["alert-icon"]}>⚠️</span>
            <span>{error}</span>
            <button 
              className={styles["alert-close"]} 
              onClick={() => setError('')}
            >
              ✕
            </button>
          </div>
        )}

        {/* INFO CONVERSATION */}
        <div className={styles["conversation-info-card"]}>
          <div className={styles["info-row"]}>
            <span className={styles["info-label"]}>
              {t('conversation:info.objective_label')}
            </span>
            <span className={styles["info-value"]}>
              {girlInfo?.objective === 'discussion' && t('conversation:info.objective_discussion')}
              {girlInfo?.objective === 'numero' && t('conversation:info.objective_numero')}
              {girlInfo?.objective === 'date' && t('conversation:info.objective_date')}
              {girlInfo?.objective === 'flexible' && t('conversation:info.objective_flexible')}
            </span>
          </div>
          {girlInfo?.key_info && (
            <div className={styles["info-row"]}>
              <span className={styles["info-label"]}>
                {t('conversation:info.key_info_label')}
              </span>
              <span className={styles["info-value"]}>{girlInfo.key_info}</span>
            </div>
          )}
        </div>

        {/* ============ HISTORIQUE MESSAGERIE ============ */}
        {history.length > 0 && (
          <div className={styles["history-section"]}>
            <h3 className={styles["history-title"]}>
              {t('conversation:history.title')}
            </h3>
            
            <div className={styles["messages-timeline"]}>
              {history.map((interaction) => (
                <div key={interaction.id} className={styles["interaction-block"]}>
                  
                  {/* ICE BREAKER */}
                  {interaction.interaction_type === 'ice_breaker' && (
                    <div className={styles["message-group"]}>
                      <div className={styles["message-wingman"]}>
                        <div className={styles["message-avatar"]}>🤖</div>
                        <div className={`${styles["message-bubble"]} ${styles["wingman"]}`}>
                          <strong>
                            {t('conversation:history.icebreaker_generated')}
                          </strong>
                          <p>
                            {t('conversation:history.style_label')}{' '}
                            {interaction.style_chosen}
                          </p>
                          {interaction.message_used_content && (
                            <div className={styles["message-used"]}>
                              {t('conversation:history.icebreaker_used', {
                                message: interaction.message_used_content,
                              })}
                            </div>
                          )}
                        </div>
                      </div>
                      <div className={styles["message-timestamp"]}>
                        {new Date(interaction.created_at).toLocaleString()}
                      </div>
                    </div>
                  )}

{interaction.interaction_type === 'date_planning' && interaction.message_used_content && (
  <div className={styles["message-group"]}>
    <div className={styles["message-user"]}>
      <div className={`${styles["message-bubble"]} ${styles["user"]}`}>
        <strong>📅 {t('conversation:history.date_planning_sent')}</strong>
        <p>"{interaction.message_used_content}"</p>
      </div>
      <div className={styles["message-avatar"]}>👤</div>
    </div>
    <div className={styles["message-timestamp"]}>
      {new Date(interaction.created_at).toLocaleString()}
    </div>
  </div>
)}

                  {/* ANALYZE + SUGGESTIONS */}
                  {interaction.girl_message && (
                    <div className={styles["message-group"]}>
                      {/* Message de la fille */}
                      <div className={styles["message-girl"]}>
                        <div className={`${styles["message-bubble"]} ${styles["girl"]}`}>
                          <strong>
                            {girlInfo?.name || t('conversation:history.her')}
                          </strong>
                          <p>"{interaction.girl_message}"</p>
                        </div>
                        <div className={styles["message-avatar"]}>👧</div>
                      </div>
                      
                      {/* Analyse Wingman */}
                      {interaction.analysis_summary && (
                        <div className={styles["message-wingman"]}>
                          <div className={styles["message-avatar"]}>🤖</div>
                          <div className={`${styles["message-bubble"]} ${styles["wingman"]} ${styles["analysis"]}`}>
                            <strong>
                              {t('conversation:history.wingman_analysis_title')}
                            </strong>
                            <p>{interaction.analysis_summary}</p>
                            <small>
                              {t('conversation:history.emotion_label')}{' '}
                              {interaction.detected_emotion}
                            </small>
                          </div>
                        </div>
                      )}

                      {/* Message utilisé */}
                      {interaction.message_used_content && (
                        <div className={styles["message-user"]}>
                          <div className={`${styles["message-bubble"]} ${styles["user"]}`}>
                            <strong>
                              {t('conversation:history.you_used_message_title')}
                            </strong>
                            <p>"{interaction.message_used_content}"</p>
                          </div>
                          <div className={styles["message-avatar"]}>👤</div>
                        </div>
                      )}

                      <div className={styles["message-timestamp"]}>
                        {new Date(interaction.created_at).toLocaleString()}
                      </div>
                    </div>
                  )}

                </div>
              ))}
            </div>
          </div>
        )}

        {/* ============ MODE ICE BREAKER ============ */}
        {mode === 'ice_breaker' && (
          <>
            {showStyleChoiceIceBreaker && iceBreakers.length === 0 && (
              <div className={styles["ice-breaker-state"]}>
                <div className={styles["wingman-welcome"]}>
                  <div className={styles["wingman-avatar-large"]}>🤖</div>
                  <h3>{t('conversation:ice_breaker.title')}</h3>
                  <p>{t('conversation:ice_breaker.subtitle')}</p>
                </div>

                <div className={styles["style-grid"]}>
                  <button
                    className={`${styles["style-card"]} ${styles["humour"]}`}
                    onClick={() => handleGenerateIceBreakers('humour')}
                    disabled={loadingIceBreaker}
                  >
                    <div className={styles["style-icon"]}>😄</div>
                    <div className={styles["style-content"]}>
                      <h4>{t('conversation:styles.humour_title')}</h4>
                      <p>{t('conversation:styles.humour_subtitle')}</p>
                    </div>
                  </button>
                  <button
                    className={`${styles["style-card"]} ${styles["direct"]}`}
                    onClick={() => handleGenerateIceBreakers('direct')}
                    disabled={loadingIceBreaker}
                  >
                    <div className={styles["style-icon"]}>💬</div>
                    <div className={styles["style-content"]}>
                      <h4>{t('conversation:styles.direct_title')}</h4>
                      <p>{t('conversation:styles.direct_subtitle')}</p>
                    </div>
                  </button>
                  <button
                    className={`${styles["style-card"]} ${styles["taquin"]}`}
                    onClick={() => handleGenerateIceBreakers('taquin')}
                    disabled={loadingIceBreaker}
                  >
                    <div className={styles["style-icon"]}>🔥</div>
                    <div className={styles["style-content"]}>
                      <h4>{t('conversation:styles.taquin_title')}</h4>
                      <p>{t('conversation:styles.taquin_subtitle')}</p>
                    </div>
                  </button>
                  <button
                    className={`${styles["style-card"]} ${styles["mysterieux"]}`}
                    onClick={() => handleGenerateIceBreakers('mysterieux')}
                    disabled={loadingIceBreaker}
                  >
                    <div className={styles["style-icon"]}>🤔</div>
                    <div className={styles["style-content"]}>
                      <h4>{t('conversation:styles.mysterieux_title')}</h4>
                      <p>{t('conversation:styles.mysterieux_subtitle')}</p>
                    </div>
                  </button>
                </div>

                {loadingIceBreaker && (
                  <div className={styles["loading-inline"]}>
                    <div className={styles["spinner"]}></div>
                    <span>{t('conversation:ice_breaker.generating')}</span>
                  </div>
                )}
              </div>
            )}

            {iceBreakers.length > 0 && (
              <div className={styles["ice-breakers-list"]}>
                <div className={styles["suggestions-header"]}>
                  <h3>{t('conversation:ice_breaker.select_suggestion')}</h3>
                </div>
                <div className={styles["suggestions-list"]}>
                  {iceBreakers.map((iceBreaker, index) => (
                    <div key={iceBreaker.id} className={styles["suggestion-card"]}>
                      <div className={styles["suggestion-header"]}>
                        <span className={styles["suggestion-number"]}>
                          {t('conversation:ice_breaker.message_number', {
                            number: index + 1,
                          })}
                        </span>
                        <span className={styles["suggestion-tone"]}>
                          {iceBreaker.tone}
                        </span>
                      </div>
                      <div className={styles["suggestion-content"]}>
                        <p>{iceBreaker.content}</p>
                      </div>
                      {iceBreaker.success_rate && (
                        <div className={styles["suggestion-stats"]}>
                          <span>
                            {t('conversation:ice_breaker.success_rate', {
                              percent: Math.round(iceBreaker.success_rate * 100),
                            })}
                          </span>
                        </div>
                      )}
                      <div className={styles["suggestion-actions"]}>
                        <button
                          className={styles["btn-copy"]}
                          onClick={() => handleCopy(iceBreaker.content)}
                        >
                          {t('conversation:ice_breaker.copy')}
                        </button>
                        <button
                          className={styles["btn-use"]}
                          onClick={() =>
                            handleUseIceBreaker(iceBreaker.content, iceBreaker.id)
                          }
                        >
                          {t('conversation:ice_breaker.mark_sent')}
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}

        {/* ============ MODE WAITING RESPONSE ============ */}
        {mode === 'waiting_response' && (
          <div className={styles["waiting-response-state"]}>
            <div className={styles["wingman-waiting"]}>
              <div className={styles["wingman-avatar-large"]}>🤖</div>
              <h3>{t('conversation:modes.waiting_response')}</h3>
            </div>
            <div className={styles["waiting-action"]}>
              <button
                className={styles["btn-primary"]}
                onClick={handleSheReplied}
              >
                {t('conversation:active_chat.she_replied')}
              </button>
            </div>
          </div>
        )}

        {/* ============ MODE ACTIVE CHAT ============ */}
        {mode === 'active_chat' && (
          <>
            {/* État initial OU restauré avec analyse */}
            {!wingmanLoading && suggestions.length === 0 && (
              <>
                {/* Si pas d'analyse, afficher input */}
                {!wingmanAnalysis && (
                  <div className={styles["wingman-initial-state"]}>
                    <div className={styles["wingman-welcome"]}>
                      <div className={styles["wingman-avatar-large"]}>🤖</div>
                      <h3>{t('conversation:active_chat.paste_last_message')}</h3>
                    </div>

                    {/* Toggle text/image */}
                    <div className={styles["upload-mode-toggle"]}>
                      <button 
                        className={`${styles["toggle-btn"]} ${uploadMode === 'text' ? styles["active"] : ''}`}
                        onClick={() => handleToggleUploadMode('text')}
                      >
                        {t('conversation:active_chat.mode_text')}
                      </button>
                      <button 
                        className={`${styles["toggle-btn"]} ${uploadMode === 'image' ? styles["active"] : ''}`}
                        onClick={() => handleToggleUploadMode('image')}
                      >
                        {t('conversation:active_chat.mode_image')}
                      </button>
                    </div>

                    {/* MODE TEXTE */}
                    {uploadMode === 'text' && (
                      <div className={styles["message-input-section"]}>
                        <textarea
                          className={styles["girl-message-input"]}
                          placeholder={t('conversation:active_chat.message_placeholder')}
                          value={girlMessage}
                          onChange={(e) => setGirlMessage(e.target.value)}
                          rows={4}
                        />
                        <button 
                          className={styles["btn-analyze"]} 
                          onClick={handleAnalyze} 
                          disabled={!girlMessage.trim()}
                        >
                          {t('conversation:active_chat.analyze_button')}
                        </button>
                      </div>
                    )}

                    {/* MODE IMAGE */}
                    {uploadMode === 'image' && (
                      <div className={styles["image-upload-section"]}>
                        {!imagePreview ? (
                          <>
                            <input
                              ref={fileInputRef}
                              type="file"
                              accept="image/*"
                              onChange={handleImageSelect}
                              style={{ display: 'none' }}
                            />
                            <button 
                              className={styles["btn-upload-image"]}
                              onClick={() => fileInputRef.current?.click()}
                            >
                              <span className={styles["upload-icon"]}>📷</span>
                              <span>{t('conversation:image.upload_button')}</span>
                              <small>
                                {t('conversation:image.allowed_formats')}
                              </small>
                            </button>
                            <p className={styles["upload-hint"]}>
                              {t('conversation:image.hint')}
                            </p>
                          </>
                        ) : (
                          <div className={styles["image-preview-container"]}>
                            <div className={styles["image-preview"]}>
                              <img src={imagePreview} alt="Preview" />
                              <button 
                                className={styles["btn-remove-image"]}
                                onClick={handleRemoveImage}
                              >
                                ✕
                              </button>
                            </div>
                            <button 
                              className={styles["btn-analyze-image"]} 
                              onClick={handleAnalyzeFromImage}
                            >
                              {t('conversation:image.analyze_button')}
                            </button>
                            <p className={styles["credits-info"]}>
                              {t('conversation:image.credits_info')}
                            </p>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}

                {/* Si analyse existe, afficher l'analyse */}
               {/* Si analyse existe, afficher l'analyse */}
{wingmanAnalysis && !showStyleChoice && (
  <div className={styles["wingman-analysis-state"]}>

    {/* Message de la fille */}
    <div className={styles["girl-message-card"]}>
      <div className={`${styles["message-bubble"]} ${styles["girl"]}`}>
        "{girlMessage}"
      </div>
    </div>

    {/* ══════════════════════════════════════
        WINGMAN ANALYSIS CARD — Nouvelle UI
        ══════════════════════════════════════ */}
    <div className={styles["wingman-analysis-card"]}>

      {/* Label WINGMAN + verdict */}
      <div className={styles["analysis-wingman-label"]}>WINGMAN</div>
      <p className={styles["analysis-verdict"]}>{wingmanAnalysis.verdict || wingmanAnalysis.summary}</p>

      {/* ── Métriques Interest + Ghost Risk ── */}
      <div className={styles["analysis-metrics-row"]}>
        <div className={styles["metric-item"]}>
          <span className={styles["metric-label"]}>INTEREST</span>
          <span className={`${styles["metric-badge"]} ${styles["metric-" + (wingmanAnalysis.interest_level || 'medium')]}`}>
            {(wingmanAnalysis.interest_level || 'medium').charAt(0).toUpperCase() + (wingmanAnalysis.interest_level || 'medium').slice(1)}
          </span>
        </div>
        <div className={styles["metric-item"]}>
          <span className={styles["metric-label"]}>GHOST RISK</span>
          <span className={`${styles["metric-badge"]} ${styles["metric-ghost-" + (wingmanAnalysis.ghost_risk || 'low')]}`}>
            {(wingmanAnalysis.ghost_risk || 'low').charAt(0).toUpperCase() + (wingmanAnalysis.ghost_risk || 'low').slice(1)}
          </span>
        </div>
        {wingmanAnalysis.next_move && (
          <div className={styles["metric-next-move"]}>
            <span className={styles["metric-label"]}>NEXT</span>
            <span className={styles["metric-next-text"]}>{wingmanAnalysis.next_move}</span>
          </div>
        )}
      </div>

      {/* ── Alerte Ghosting (conservée) ── */}
      {wingmanAnalysis.ghosting_alert?.is_ghosting && (
        <div className={`${styles["ghosting-alert"]} ${styles["ghosting-" + wingmanAnalysis.ghosting_alert.ghosting_level]}`}>
          <div className={styles["alert-header"]}>
            {wingmanAnalysis.ghosting_alert.ghosting_level === 'critical' && t('conversation:ghosting.critical')}
            {wingmanAnalysis.ghosting_alert.ghosting_level === 'high' && t('conversation:ghosting.high')}
            {wingmanAnalysis.ghosting_alert.ghosting_level === 'medium' && t('conversation:ghosting.medium')}
            {wingmanAnalysis.ghosting_alert.ghosting_level === 'low' && t('conversation:ghosting.low')}
          </div>
          <div className={styles["red-flags"]}>
            <strong>{t('conversation:ghosting.red_flags_title')}</strong>
            <ul>
              {wingmanAnalysis.ghosting_alert.red_flags?.map((flag, i) => (
                <li key={i}>{flag}</li>
              ))}
            </ul>
          </div>
          <div className={styles["strategy-info"]}>
            <strong>{t('conversation:ghosting.strategy_title')}</strong>
            {wingmanAnalysis.ghosting_alert.strategy === 'relance_piquante' && (
              <p>{t('conversation:ghosting.strategy_relance_piquante')}</p>
            )}
            {wingmanAnalysis.ghosting_alert.strategy === 'derniere_chance' && (
              <p>{t('conversation:ghosting.strategy_derniere_chance')}</p>
            )}
            {wingmanAnalysis.ghosting_alert.strategy === 'sortie_elegante' && (
              <p>{t('conversation:ghosting.strategy_sortie_elegante')}</p>
            )}
          </div>
        </div>
      )}

      {/* ── Alerte Date Readiness (conservée) ── */}
      {wingmanAnalysis.date_readiness_alert?.should_suggest_date && (
        <div className={styles["date-readiness-banner"]}>
          <div className={styles["banner-icon"]}>💡</div>
          <div className={styles["banner-content"]}>
            <div className={styles["banner-header"]}>
              <strong>{t('conversation:date_readiness.title')}</strong>
              <span className={styles["score-badge"]}>
                {Math.round(wingmanAnalysis.date_readiness_alert.score_estimate * 100)}/100
              </span>
            </div>
            <p className={styles["banner-reason"]}>
              {wingmanAnalysis.date_readiness_alert.reasoning}
            </p>
            {wingmanAnalysis.date_readiness_alert.key_signals && (
              <div className={styles["key-signals"]}>
                {wingmanAnalysis.date_readiness_alert.key_signals.map((signal, i) => (
                  <span key={i} className={styles["signal-tag"]}>✓ {signal}</span>
                ))}
              </div>
            )}
          </div>
          <button
            className={styles["btn-date-planning"]}
            onClick={handleOpenDatePlanning}
          >
            {t('conversation:date_readiness.button')}
          </button>
        </div>
      )}

      {/* ── Indicateurs détaillés (repliables) ── */}
      {wingmanAnalysis.indicators?.length > 0 && (
        <div className={styles["analysis-indicators"]}>
          {wingmanAnalysis.indicators.map((ind, i) => (
            <span key={i} className={styles["indicator-tag"]}>• {ind}</span>
          ))}
        </div>
      )}

      {/* ── Recommandation ── */}
      {wingmanAnalysis.recommendation && (
        <div className={styles["analysis-reco"]}>
          <span className={styles["reco-label"]}>{t('conversation:analysis.reco_title')}</span>
          <p>{wingmanAnalysis.recommendation}</p>
        </div>
      )}
    </div>

    {/* Boutons d'action (conservés à l'identique) */}
    <div className={styles["action-buttons"]}>
      {wingmanAnalysis.ghosting_alert?.strategy === 'sortie_elegante' ? (
        <>
          <button className={styles["btn-secondary"]} onClick={handleShowStyleChoice}>
            {t('conversation:buttons.exit_message')}
          </button>
          <button className={styles["btn-primary"]} onClick={() => navigate('/dashboard')}>
            {t('conversation:buttons.go_to_other_girl')}
          </button>
        </>
      ) : (
        <>
          <button className={styles["btn-primary"]} onClick={handleShowStyleChoice}>
            {wingmanAnalysis.ghosting_alert?.strategy === 'relance_piquante' && t('conversation:buttons.relance_piquante')}
            {wingmanAnalysis.ghosting_alert?.strategy === 'derniere_chance' && t('conversation:buttons.derniere_chance')}
            {(!wingmanAnalysis.ghosting_alert || wingmanAnalysis.ghosting_alert.strategy === 'continue') && t('conversation:buttons.generate_answers')}
          </button>
          <button className={styles["btn-secondary"]} onClick={handleNewInteraction}>
            {t('conversation:buttons.write_myself')}
          </button>
        </>
      )}
    </div>

  </div>
)}
              </>
            )}

            {/* Choix du style */}
            {showStyleChoice && (
             <div className={styles["style-grid"]}>
  <button className={`${styles["style-card"]} ${styles["humour"]}`} onClick={() => handleGenerate('humour')} disabled={wingmanLoading}>
    😄 {t('conversation:styles.humour_title')}
  </button>
  <button className={`${styles["style-card"]} ${styles["direct"]}`} onClick={() => handleGenerate('direct')} disabled={wingmanLoading}>
    💬 {t('conversation:styles.direct_title')}
  </button>
  <button className={`${styles["style-card"]} ${styles["taquin"]}`} onClick={() => handleGenerate('taquin')} disabled={wingmanLoading}>
    😏 {t('conversation:styles.taquin_title')}
  </button>
  <button className={`${styles["style-card"]} ${styles["mysterieux"]}`} onClick={() => handleGenerate('mysterieux')} disabled={wingmanLoading}>
    🤔 {t('conversation:styles.mysterieux_title')}
  </button>
  {/* ⭐ NOUVEAU */}
  <button className={`${styles["style-card"]} ${styles["rage_bait"]}`} onClick={() => handleGenerate('rage_bait')} disabled={wingmanLoading}>
    🔥 {t('conversation:styles.rage_bait_title')}
  </button>
</div>
            )}

            {/* Suggestions */}
            {suggestions.length > 0 && (
              <div className={styles["suggestions-state"]}>
                <h3>{t('conversation:suggestions.title')}</h3>
                <div className={styles["suggestions-list"]}>
                  {suggestions.map((sug, index) => (
                    <div key={sug.id} className={styles["suggestion-card"]}>
                      <div className={styles["suggestion-header"]}>
                        <span>
                          {t('conversation:suggestions.option_number', {
                            number: index + 1,
                          })}
                        </span>
                        <span>{sug.tone}</span>
                      </div>
                      <div className={styles["suggestion-content"]}>
                        <p>{sug.content}</p>
                      </div>
                      <div className={styles["suggestion-actions"]}>
                        <button
                          className={styles["btn-copy"]}
                          onClick={() => handleCopy(sug.content)}
                        >
                          {t('conversation:ice_breaker.copy')}
                        </button>
                        <button
                          className={styles["btn-use"]}
                          onClick={() => handleUseMessage(sug.content, sug.id)}
                        >
                          {t('conversation:ice_breaker.mark_sent')}
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
                <button
                  className={styles["btn-new-interaction"]}
                  onClick={handleNewInteraction}
                >
                  {t('conversation:suggestions.new_interaction')}
                </button>
              </div>
            )}
          </>
        )}

        {/* Scroll anchor */}
        <div ref={messagesEndRef} />

      </div>

    </div>
  );
};

export default Conversation;